import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { InlineWarning, NotConfiguredState, Panel } from "@/components/common/Primitives";
import { SORT_OPTIONS, sortAnalyses } from "@/components/OpportunityTable";
import { NavTree } from "@/components/opportunities/NavTree";
import { KpiBar } from "@/components/opportunities/KpiBar";
import { GroupedView } from "@/components/opportunities/GroupedView";
import { OpportunityRows } from "@/components/opportunities/OpportunityRows";
import { ProfilesView } from "@/components/opportunities/ProfilesView";
import { RiskTable } from "@/components/risks/RiskTable";
import { ChangesView } from "@/components/opportunities/ChangesView";
import { QuickActions, RecentScans, StatusLegend } from "@/components/opportunities/SidePanels";
import {
  OpportunityNotices,
  OpportunityTabs,
} from "@/components/opportunities/OpportunityNotices";
import { loadColumns, saveColumns } from "@/components/opportunities/columns";
import { QUICK_FILTERS } from "@/components/opportunities/quickFilters";
import { buildTree, marketStatusOf, matchesSelection, rowStats, selectionLabel } from "@/lib/grouping";
import { statusMeta } from "@/lib/marketStatus";
import { api, apiError } from "@/lib/api";
import { dateTime, eur, num } from "@/lib/format";

export default function Opportunities() {
  const [params] = useSearchParams();
  const scanId = params.get("scan");

  const [rows, setRows] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [scan, setScan] = useState(null);
  const [scanList, setScanList] = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [refreshRun, setRefreshRun] = useState(null);

  const [view, setView] = useState(scanId ? "all" : "groups");
  const [selection, setSelection] = useState({});
  const [statusFilter, setStatusFilter] = useState(null);
  const [profileFilter, setProfileFilter] = useState(null);
  const [onlyOpportunities, setOnlyOpportunities] = useState(false);
  const [quick, setQuick] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [sortKey, setSortKey] = useState("score");
  const [columns, setColumns] = useState(loadColumns);

  const [changes, setChanges] = useState({ period: "since_last_scan", events: [], since: null, loading: false });
  const [polling, setPolling] = useState(false);
  const [refreshingKey, setRefreshingKey] = useState(null);
  const [refreshingProfile, setRefreshingProfile] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError("");
    try {
      if (scanId) {
        const { data } = await api.get(`/scans/${scanId}/results`);
        setScan(data.scan);
        setRows(data.results);
        setThresholds(data.scan?.filters || {});
      } else {
        const [opportunities, scans, profiles] = await Promise.all([
          api.get("/opportunities", {
            params: { only_opportunities: onlyOpportunities, include_disappeared: true, limit: 2000 },
          }),
          api.get("/scans", { params: { limit: 6 } }),
          api.get("/search-profiles/overview"),
        ]);
        // A refresh rewrites analyses in place: never blank out a populated screen mid-scan.
        setRows((prev) =>
          background && opportunities.data.results.length === 0 && prev.length
            ? prev
            : opportunities.data.results
        );
        setThresholds(opportunities.data.thresholds);
        if (opportunities.data.risk_config?.hide_high_risk) {
          setQuick((current) => (current.includes("norisk") ? current : [...current, "norisk"]));
        }
        setScanList(scans.data);
        setProfileList(profiles.data.profiles || []);
      }
      setLoadedAt(new Date().toISOString());
    } catch (e) {
      setError(apiError(e));
    }
    setLoading(false);
  }, [scanId, onlyOpportunities]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (view !== "changes") return;
    let cancelled = false;
    setChanges((s) => ({ ...s, loading: true }));
    api
      .get("/opportunities/changes", { params: { period: changes.period } })
      .then(({ data }) => {
        if (cancelled) return;
        setChanges((s) => ({ ...s, events: data.events || [], since: data.since, loading: false }));
      })
      .catch((e) => {
        if (cancelled) return;
        setChanges((s) => ({ ...s, loading: false }));
        toast.error(apiError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [view, changes.period]);

  useEffect(() => {
    if (!polling) return undefined;
    const tick = async () => {
      try {
        const [scans, status] = await Promise.all([
          api.get("/scans", { params: { limit: 6 } }),
          api.get("/opportunities/refresh-status"),
        ]);
        setScanList(scans.data);
        setRefreshRun(status.data.run);
        const busyScan = scans.data.some((s) => ["QUEUED", "RUNNING"].includes(s.status));
        const busyRun = status.data.run?.status === "RUNNING";
        if (!busyScan && !busyRun) {
          setPolling(false);
          load(true);
        }
      } catch (e) {
        // Een mislukte poll is bijna altijd tijdelijk (verversing bezig): blijven pollen,
        // maar wel zichtbaar in de console voor diagnose.
        console.warn("Statuspolling mislukte, wordt opnieuw geprobeerd:", apiError(e));
      }
    };
    const timer = setInterval(tick, 6000);
    return () => clearInterval(timer);
  }, [polling, load]);

  const applyColumns = (keys) => {
    setColumns(keys);
    saveColumns(keys);
  };

  const quickFiltered = useMemo(() => {
    let out = rows;
    quick.forEach((key) => {
      const filter = QUICK_FILTERS.find((f) => f.key === key);
      if (filter) out = out.filter(filter.test);
    });
    if (profileFilter) {
      out = out.filter((r) =>
        (r.market?.search_profiles || []).some((p) => p.id === profileFilter)
      );
    }
    return out;
  }, [rows, quick, profileFilter]);

  const statusScoped = useMemo(() => {
    if (statusFilter) return quickFiltered.filter((r) => marketStatusOf(r) === statusFilter);
    return quickFiltered.filter((r) => marketStatusOf(r) !== "VERDWENEN");
  }, [quickFiltered, statusFilter]);

  const treeRows = statusScoped;
  const tree = useMemo(() => buildTree(treeRows), [treeRows]);

  const visibleRows = useMemo(
    () => statusScoped.filter((r) => matchesSelection(r, selection)),
    [statusScoped, selection]
  );
  const groupedTree = useMemo(() => buildTree(visibleRows), [visibleRows]);

  const kpis = useMemo(() => {
    const scoped = quickFiltered.filter((r) => matchesSelection(r, selection));
    const stats = rowStats(scoped);
    return { ...stats, count: scoped.filter((r) => marketStatusOf(r) !== "VERDWENEN").length };
  }, [quickFiltered, selection]);

  const savedCount = useMemo(() => rows.filter((r) => r.saved).length, [rows]);
  const changeCount = kpis.new_count + kpis.price_drop_count + kpis.disappeared_count + kpis.back_online_count;
  const sortRows = useCallback((items) => sortAnalyses(items, sortKey), [sortKey]);

  const tariffGaps = useMemo(() => {
    const withGap = visibleRows.filter((r) => r.headline?.bpm_status === "TARIEF_ONTBREEKT");
    const years = [
      ...new Set(
        withGap
          .map((r) => String(r.vehicle_snapshot?.first_registration || "").slice(0, 4))
          .filter(Boolean)
      ),
    ].sort();
    return { count: withGap.length, years };
  }, [visibleRows]);

  const manualBpmCount = useMemo(
    () =>
      visibleRows.filter((r) => r.headline?.bpm_source === "MANUAL" || r.bpm?.manual_override)
        .length,
    [visibleRows]
  );

  const riskCount = useMemo(
    () => visibleRows.filter((r) => (r.red_flags?.counts?.high || 0) > 0).length,
    [visibleRows]
  );

  const providerFailures = useMemo(() => {
    const out = [];
    scanList
      .filter((s) => s.status === "COMPLETED")
      .slice(0, 3)
      .forEach((s) => {
        const failures = (s.provider_results || []).filter((p) => p.status !== "OK");
        if (failures.length === 0 && (s.provider_failures || 0) > 0) {
          out.push({
            label: "Marktbron",
            message: "Live marktdata kon niet worden opgehaald tijdens de laatste verversing.",
            scan: s.profile_name,
          });
        }
        failures.forEach((p) => {
          if (!out.some((item) => item.message === p.message)) {
            out.push({ label: p.label || p.provider, message: p.message, scan: s.profile_name });
          }
        });
      });
    return out;
  }, [scanList]);

  const refreshGroup = async (keys, key) => {
    setRefreshingKey(key);
    try {
      await api.post("/opportunities/refresh-group", keys);
      toast.success("Verversing van deze groep gestart. Nieuwe marktdata volgt automatisch.");
      setPolling(true);
    } catch (e) {
      toast.error(apiError(e));
    }
    setRefreshingKey(null);
  };

  const refreshAll = async () => {
    try {
      const { data } = await api.post("/opportunities/refresh-all");
      setRefreshRun(data);
      setPolling(true);
      toast.success(`Alles verversen gestart voor ${data.total} zoekopdracht(en).`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const refreshProfile = async (profileId) => {
    setRefreshingProfile(profileId);
    try {
      await api.post(`/search-profiles/${profileId}/refresh`);
      toast.success("Zoekopdracht wordt opnieuw uitgevoerd.");
      setPolling(true);
    } catch (e) {
      toast.error(apiError(e));
    }
    setRefreshingProfile(null);
  };

  const renameProfile = async (profileId, name) => {
    try {
      await api.patch(`/search-profiles/${profileId}`, { name });
      setProfileList((list) =>
        list.map((p) => (p.id === profileId ? { ...p, name } : p))
      );
      toast.success("Naam bijgewerkt.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const providerIssues = (scan?.provider_results || []).filter((p) => p.status !== "OK");
  const selectionText = selectionLabel(selection, tree);
  const activeProfile = profileList.find((p) => p.id === profileFilter);

  const TABS = [
    { key: "all", label: `Alle (${num(kpis.count)})` },
    { key: "groups", label: "Per merk/model" },
    { key: "profiles", label: "Per zoekopdracht" },
    { key: "risks", label: `Risico's (${num(riskCount)})` },
    { key: "changes", label: `Nieuw & gewijzigd (${num(changeCount)})` },
  ];

  // Hoofdcontent per weergave — early returns i.p.v. een geneste ternary.
  const renderMain = () => {
    if (loading) {
      return (
        <Panel testid="opportunities-panel">
          <div className="px-4 py-10 text-center text-[13px] text-slate-500">Laden…</div>
        </Panel>
      );
    }
    if (rows.length === 0) {
      return (
        <Panel testid="opportunities-panel">
          <div className="p-3">
            <NotConfiguredState
              title="Geen voertuigen om te tonen"
              description="Er zijn nog geen geanalyseerde advertenties. Er wordt geen demo- of fictieve data gegenereerd: start een scan of ververs een bestaande zoekopdracht om echte AutoScout24-advertenties op te halen."
              testid="opportunities-empty"
            />
          </div>
        </Panel>
      );
    }
    if (view === "groups") {
      return (
        <>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border border-slate-200 bg-white px-3 py-2">
            <span className="label-xs">Gegroepeerd op: merk › model › type</span>
            <label className="flex items-center gap-2 text-[12px] font-medium text-slate-700">
              <input
                type="checkbox"
                checked={showSummary}
                onChange={(e) => setShowSummary(e.target.checked)}
                data-testid="toggle-group-summary"
                className="h-3.5 w-3.5 accent-blue-600"
              />
              Toon groepssamenvatting
            </label>
          </div>
          <GroupedView
            tree={groupedTree}
            columns={columns}
            showSummary={showSummary}
            sortRows={sortRows}
            selection={selection}
            onRefreshGroup={refreshGroup}
            refreshingKey={refreshingKey}
            onChanged={load}
          />
        </>
      );
    }
    if (view === "profiles") {
      return (
        <ProfilesView
          profiles={profileList}
          onRefresh={refreshProfile}
          onRename={renameProfile}
          refreshingId={refreshingProfile}
          onOpen={(id) => {
            setProfileFilter(id);
            setView("all");
          }}
        />
      );
    }
    if (view === "risks") {
      return <RiskTable />;
    }
    if (view === "changes") {
      return (
        <ChangesView
          period={changes.period}
          since={changes.since}
          events={changes.events}
          loading={changes.loading}
          onPeriod={(period) => setChanges((s) => ({ ...s, period }))}
        />
      );
    }
    return (
      <Panel
        title={`${num(visibleRows.length)} voertuigen`}
        subtitle={`Drempel: score ≥ ${thresholds?.min_deal_score ?? 65} · bijdrage ≥ ${eur(
          thresholds?.min_contribution,
        )}`}
        testid="opportunities-panel"
      >
        {visibleRows.length === 0 ? (
          <div className="px-4 py-8 text-center text-[12.5px] text-slate-500">
            Geen voertuigen die aan deze filters voldoen.
          </div>
        ) : (
          <OpportunityRows
            rows={sortRows(visibleRows)}
            columns={columns}
            onChanged={load}
            testid="opportunities-table"
          />
        )}
      </Panel>
    );
  };

  return (
    <AppShell
      title="Handelskansen"
      subtitle={
        scanId
          ? `Resultaten van scan ${scan?.profile_name || scanId.slice(-6)}`
          : "Actuele markt & importkansen"
      }
      actions={
        <>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            data-testid="sort-select"
            className="h-8 rounded-sm border border-slate-300 bg-white px-2 text-[12px]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
          {!scanId && (
            <label className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700">
              <input
                type="checkbox"
                checked={onlyOpportunities}
                data-testid="only-opportunities-toggle"
                onChange={(e) => setOnlyOpportunities(e.target.checked)}
                className="h-3.5 w-3.5 accent-blue-600"
              />
              Alleen interessante kansen
            </label>
          )}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            data-testid="toggle-filters"
            className={`flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-[12px] font-semibold transition-colors duration-150 ${
              showFilters || quick.length
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
            {quick.length > 0 ? ` (${quick.length})` : ""}
          </button>
          <button
            type="button"
            onClick={scanId ? load : refreshAll}
            data-testid="refresh-opportunities"
            className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${polling ? "animate-spin" : ""}`} />
            {scanId ? "Verversen" : "Alles verversen"}
          </button>
        </>
      }
    >
      <OpportunityNotices
        error={error}
        providerIssues={providerIssues}
        providerFailures={providerFailures}
        refreshRun={refreshRun}
      />

      {!scanId && <OpportunityTabs tabs={TABS} view={view} onSelect={setView} />}

      {showFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="quick-filters">
          {QUICK_FILTERS.map((f) => {
            const on = quick.includes(f.key);
            return (
              <button
                key={f.key}
                type="button"
                data-testid={`quick-filter-${f.key}`}
                onClick={() => setQuick((s) => (on ? s.filter((k) => k !== f.key) : [...s, f.key]))}
                className={`rounded-sm border px-2 py-1 text-[11px] font-semibold transition-colors duration-150 ${
                  on
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                {f.label}
              </button>
            );
          })}
          {quick.length > 0 && (
            <button
              type="button"
              onClick={() => setQuick([])}
              className="px-2 py-1 text-[11px] font-semibold text-blue-700 hover:underline"
            >
              Filters wissen
            </button>
          )}
        </div>
      )}

      {(statusFilter || selectionText || activeProfile) && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="active-filters">
          {selectionText && (
            <button
              type="button"
              onClick={() => setSelection({})}
              data-testid="clear-selection"
              className="chip border-blue-300 bg-blue-50 normal-case tracking-normal text-blue-800"
            >
              {selectionText} <X className="h-3 w-3" />
            </button>
          )}
          {statusFilter && (
            <button
              type="button"
              onClick={() => setStatusFilter(null)}
              data-testid="clear-status-filter"
              className="chip border-slate-300 bg-slate-100 normal-case tracking-normal text-slate-700"
            >
              Status: {statusMeta(statusFilter).label} <X className="h-3 w-3" />
            </button>
          )}
          {activeProfile && (
            <button
              type="button"
              onClick={() => setProfileFilter(null)}
              data-testid="clear-profile-filter"
              className="chip border-slate-300 bg-slate-100 normal-case tracking-normal text-slate-700"
            >
              Zoekopdracht: {activeProfile.name} <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {!scanId && <KpiBar kpis={kpis} columns={columns} onColumns={applyColumns} />}

      {tariffGaps.count > 0 && (
        <div className="mb-3">
          <InlineWarning tone="warning" testid="tariff-gap-warning">
            {tariffGaps.count} voertuig(en) hebben nog geen definitieve BPM omdat essentiële
            voertuigdata ontbreekt (CO₂ of netto-catalogusprijs). Open de BPM-kolom om de
            catalogusprijs of een definitieve BPM handmatig in te vullen.
          </InlineWarning>
        </div>
      )}

      {manualBpmCount > 0 && (
        <div className="mb-3">
          <span
            className="chip border-blue-300 bg-blue-50 normal-case tracking-normal text-blue-900"
            data-testid="manual-bpm-notice"
          >
            Handmatige BPM-referentie actief voor {manualBpmCount} voertuig(en) — heeft voorrang op
            de CO₂-berekening
          </span>
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[230px_minmax(0,1fr)] 2xl:grid-cols-[240px_minmax(0,1fr)_264px]">
        <NavTree
          tree={tree}
          total={kpis.count}
          selection={selection}
          onSelect={setSelection}
          defaultOpen={
            typeof window !== "undefined" &&
            window.matchMedia("(min-width: 1024px)").matches
          }
        />

        <div className="min-w-0">{renderMain()}</div>

        <aside className="space-y-3 lg:col-span-2 2xl:col-span-1" data-testid="opportunity-rail">
          <StatusLegend />
          <QuickActions
            kpis={kpis}
            savedCount={savedCount}
            onStatus={(key) => {
              setStatusFilter((current) => (current === key ? null : key));
              setView("all");
            }}
          />
          <RecentScans scans={scanList} />
        </aside>
      </div>

      <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Gegevens worden bijgewerkt bij elke verversing. Laatste update: {dateTime(loadedAt)}
      </div>
    </AppShell>
  );
}
